import React, { useState, ChangeEvent } from 'react';
import {
  SimpleShowLayout,
  Record,
  useTranslate,
  useNotify,
  useRedirect,
  useMutation,
} from 'react-admin';
import { Button, CircularProgress, TextField } from '@material-ui/core';
import SectionTitle from '../SectionTitle';
import CustomSelect from '../CustomSelect';
import CustomCheckbox from '../CustomCheckbox';
import messages from '../DoctorReportForm/messages';
import ConfirmationModal from '../DoctorReportForm/ConfirmationModal';

import './styles.scss';

interface RecommendedActionsConfiguratorProps {
  record: Record | undefined;
  readOnly?: boolean;
}

const getDoctorReport = (record: Record | undefined) => {
  if (!record) {
    return null;
  }

  const { services } = record.results;

  const doctorReport = services.find(
    (service: any) => service.service === 'doctorsReport',
  );

  return doctorReport;
};

const checkRecommendedAction = (recommendedActions: any, key: string) => {
  if (!recommendedActions) {
    return '';
  }

  const actionData = recommendedActions.find(
    (action: any) => action.key === key,
  );
  if (actionData) {
    return 'true';
  }

  return 'false';
};

const RecommendedActionsConfigurator: React.FC<RecommendedActionsConfiguratorProps> =
  ({ record, readOnly = false }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [openModal, setOpenModal] = useState<boolean>(false);
    const recommendedActions = record?.recommendedActions;

    const doctorReport = getDoctorReport(record);
    const [consultationCompleted, setConsultationCompleted] =
      useState<boolean>();
    // Select states
    const [retestInThreeMonths, setRetestInThreeMonths] = useState<string>(
      checkRecommendedAction(recommendedActions, 'retestInThreeMonths'),
    );
    const [consultYourGyneacologist, setConsultYourGyneacologist] =
      useState<string>(
        checkRecommendedAction(recommendedActions, 'consultYourGyneacologist'),
      );
    const [
      additionalHormoneAndFertilityTests,
      setAdditionalHormoneAndFertilityTests,
    ] = useState<string>(
      checkRecommendedAction(
        recommendedActions,
        'additionalHormoneAndFertilityTests',
      ),
    );

    // Report form states
    const [doctorName, setDoctorName] = useState<string>(
      doctorReport && doctorReport.doctorsName
        ? doctorReport.doctorsName
        : 'Dr. Alina Staikov',
    );
    const [report, setReport] = useState<string>(
      doctorReport ? doctorReport.doctorsReport : '',
    );

    // Add error states
    const [errors, setErrors] = useState({
      doctorName: false,
      report: false,
      retestInThreeMonths: false,
      consultYourGyneacologist: false,
      additionalHormoneAndFertilityTests: false,
      consultationCompleted: false,
    });

    const createRecommendedActionsArray = () => {
      const actions = [];

      if (consultYourGyneacologist === 'true') {
        actions.push({
          done: false,
          key: 'consultYourGyneacologist',
          timingKey: 'now',
        });
      }

      if (additionalHormoneAndFertilityTests === 'true') {
        actions.push({
          done: false,
          key: 'additionalHormoneAndFertilityTests',
          timingKey: 'now',
        });
      }

      if (retestInThreeMonths === 'true') {
        actions.push({
          done: false,
          key: 'retestInThreeMonths',
          timingKey: 'inThreeMonths',
        });
      }

      return actions;
    };

    const [addReport, { loading }] = useMutation(
      {
        type: 'update',
        resource: 'tests',
        payload: {
          id: record?.id,
          data: {
            report,
            doctorName,
            isPharmacyReport: false,
            pharmacyName: null,
            recommendedActions: createRecommendedActionsArray(),
          },
        },
      },
      {
        onSuccess: () => {
          redirect('/tests/fertility-check');
          notify(messages.addReportSuccessMessage, { type: 'success' });
        },
        onFailure: () =>
          notify(messages.addReportErrorMessage, { type: 'warning' }),
      },
    );

    const validateFields = () => {
      const newErrors = {
        doctorName: !doctorName || doctorName.trim() === '',
        report: !report || report.trim() === '',
        retestInThreeMonths: !retestInThreeMonths,
        consultYourGyneacologist: !consultYourGyneacologist,
        additionalHormoneAndFertilityTests: !additionalHormoneAndFertilityTests,
        consultationCompleted: !consultationCompleted,
      };

      setErrors(newErrors);
      return !Object.values(newErrors).some((error) => error);
    };

    const handleFormSubmit = () => {
      const isValid = validateFields();

      if (!isValid) {
        setOpenModal(false);
        return;
      }

      if (isValid) {
        addReport();
      }
    };

    // Select handlers
    const handleRetestInThreeMonthsChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setRetestInThreeMonths(event.target.value);
      setErrors((prev) => ({ ...prev, retestInThreeMonths: false }));
    };

    const handleConsultYourGyneacologistChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setConsultYourGyneacologist(event.target.value);
      setErrors((prev) => ({ ...prev, consultYourGyneacologist: false }));
    };

    const handleAdditionalHormoneAndFertilityTestsChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setAdditionalHormoneAndFertilityTests(event.target.value);
      setErrors((prev) => ({
        ...prev,
        additionalHormoneAndFertilityTests: false,
      }));
    };

    // Report form handlers
    const handleDoctorNameInputChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setDoctorName(event.target.value);
      setErrors((prev) => ({ ...prev, doctorName: false }));
    };

    const handleReportTextareaChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setReport(event.target.value);
      setErrors((prev) => ({ ...prev, report: false }));
    };

    const handleConsultationCompletedChange = (checked: boolean) => {
      setConsultationCompleted(checked);
      setErrors((prev) => ({ ...prev, consultationCompleted: false }));
    };

    const handleCloseModal = () => {
      setOpenModal(false);
    };
    
    const handleOpenModal = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setOpenModal(true);
    };

    return (
      <SimpleShowLayout className="recommendedActionsConfigurator">
      <>
      <ConfirmationModal
        handleClose={handleCloseModal}
        open={openModal}
        confirmationFunction={handleFormSubmit}
        loading={loading || false}
        isFertilityCheck
      />
        <form onSubmit={handleOpenModal}>
          <SectionTitle
            className="recommendedActionsConfigurator__title"
            title={translate('testResults.configureRecommendedActions')}
          />
          <div className="recommendedActionsConfigurator__item">
            <div>
              {translate(
                'fertilityCheck.configurator.consultYourGyneacologist',
              )}
            </div>
            <CustomSelect
              emptyLabel={translate('fertilityCheck.configurator.yesOrNo')}
              options={[
                {
                  value: 'true',
                  label: translate('fertilityCheck.configurator.yes'),
                },
                {
                  value: 'false',
                  label: translate('fertilityCheck.configurator.no'),
                },
              ]}
              value={consultYourGyneacologist}
              onChange={handleConsultYourGyneacologistChange}
              error={errors.consultYourGyneacologist}
              disabled={readOnly}
            />
          </div>
          <div className="recommendedActionsConfigurator__item">
            <div>
              {translate(
                'fertilityCheck.configurator.additionalHormoneAndFertilityTests',
              )}
            </div>
            <CustomSelect
              emptyLabel={translate('fertilityCheck.configurator.yesOrNo')}
              options={[
                {
                  value: 'true',
                  label: translate('fertilityCheck.configurator.yes'),
                },
                {
                  value: 'false',
                  label: translate('fertilityCheck.configurator.no'),
                },
              ]}
              value={additionalHormoneAndFertilityTests}
              onChange={handleAdditionalHormoneAndFertilityTestsChange}
              error={errors.additionalHormoneAndFertilityTests}
              disabled={readOnly}
            />
          </div>
          <div className="recommendedActionsConfigurator__item">
            <div>
              {translate('fertilityCheck.configurator.retestInThreeMonths')}
            </div>
            <CustomSelect
              emptyLabel={translate('fertilityCheck.configurator.yesOrNo')}
              options={[
                {
                  value: 'true',
                  label: translate('fertilityCheck.configurator.yes'),
                },
                {
                  value: 'false',
                  label: translate('fertilityCheck.configurator.no'),
                },
              ]}
              value={retestInThreeMonths}
              onChange={handleRetestInThreeMonthsChange}
              error={errors.retestInThreeMonths}
              disabled={readOnly}
            />
          </div>
          <div className="recommendedActionsConfigurator__item--rowDirection">
            <div>
              {translate(
                'fertilityCheck.configurator.confirmCompletedConsultation',
              )}
            </div>
            <CustomCheckbox
              label={translate(
                'fertilityCheck.configurator.consultationCompleted',
              )}
              disabled={readOnly}
              // We are not saving this info in the DB but form can't be finished without checking this box
              // So we are checking it by default if readOnly is true
              checked={consultationCompleted || readOnly}
              onChange={(event) =>
                handleConsultationCompletedChange(event.target.checked)
              }
              error={errors.consultationCompleted}
            />
          </div>

          <div className="form">
            <SectionTitle
              className="recommendedActionsConfigurator__title"
              title={translate('testResults.doctorsReport')}
            />
            <TextField
              className="input"
              id="doctor-name"
              label={translate(messages.inputLabel)}
              value={doctorName}
              variant="outlined"
              onChange={handleDoctorNameInputChange}
              error={errors.doctorName}
              helperText={
                errors.doctorName ? translate('ra.validation.required') : ''
              }
              disabled={readOnly}
            />
            <TextField
              className="textarea"
              id="report"
              label={translate(messages.textareaLabel)}
              multiline
              value={report}
              rows={8}
              variant="outlined"
              onChange={handleReportTextareaChange}
              error={errors.report}
              helperText={
                errors.report ? translate('ra.validation.required') : ''
              }
              disabled={readOnly}
            />
            {!readOnly && (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className="button"
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size="24.5px" />
                ) : (
                  translate(messages.buttonText)
                )}
              </Button>
            )}
          </div>
        </form>
        </>
      </SimpleShowLayout>
    );
  };

export default RecommendedActionsConfigurator;
